import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useToastController } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppToast, APP_TOASTER_ID } from '../../../commons/component/AppToast/AppToast.Component';
import { actionCreators } from '../../../redux/middleware/ImportJobs.Middleware';
import { AppState } from '../../../redux/reducers';
import {
  importJobConsumptionsLink,
  importJobErrorText,
  importJobRetryLink,
  isImportJobSucceeded,
  showBrowserNotification,
} from '../../../functions/ImportJobs.Functions';
import { ProviderImportJob } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

/** El aviso de fin queda a la vista más que una confirmación breve: trae una acción. */
export const IMPORT_JOB_TOAST_TIMEOUT_MS = 10000;

/**
 * Aviso global de las importaciones en segundo plano (spec 008), montado una vez en el layout principal con
 * usuario. Arranca el seguimiento (`mine` activos al cargar), y por cada job terminado pendiente de aviso:
 * - toast de la guía (`AppToast` en el Toaster global) con "Ver consumos" o "Reintentar", salvo que el asistente
 *   abierto ya lo esté mostrando;
 * - notificación del navegador si la pestaña está oculta, hay permiso y el usuario la pidió;
 * - lo marca como avisado (sessionStorage) para no repetirlo.
 * La app no tiene centro de notificaciones (campana): no se agrega entrada (spec 008, Assumptions).
 */
export const ImportJobsWatcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.jobs.notice.${key}`, options);
  const token = useSelector((state: AppState) => state.oidc?.user?.access_token);
  const jobsState = useSelector((state: AppState) => state.ImportJobs);
  const { dispatchToast, dismissToast } = useToastController(APP_TOASTER_ID);
  const handled = useRef(new Set<string>());

  useEffect(() => {
    if (token) dispatch(actionCreators.InitImportJobsWatcher() as any);
  }, [token, dispatch]);

  useEffect(
    () => () => {
      dispatch(actionCreators.StopImportJobsPolling() as any);
    },
    [dispatch]
  );

  const notice = (job: ProviderImportJob) => {
    const fileName = job.fileName?.trim() || tr('unknownFile');
    const succeeded = isImportJobSucceeded(job);
    const link = succeeded ? importJobConsumptionsLink(job) : importJobRetryLink(job);
    const title = succeeded ? tr('successTitle', { created: job.result?.created ?? 0, fileName }) : tr('failedTitle', { fileName });
    const body = succeeded
      ? tr('successBody', { total: job.result?.totalRows ?? 0, skipped: job.result?.skippedExisting ?? 0 })
      : importJobErrorText(job) || tr('failedBody');
    const toastId = `import-job-${job.id}`;
    const go = () => {
      dismissToast(toastId);
      navigate(link);
    };

    if (!(jobsState?.foregroundIds ?? []).includes(String(job.id))) {
      dispatchToast(
        <AppToast
          intent={succeeded ? 'success' : 'error'}
          title={title}
          body={body}
          data-testid={`import-job-toast-${job.id}`}
          actions={
            <Button appearance="transparent" size="small" className="app-toast__action" onClick={go} data-testid={`import-job-toast-action-${job.id}`}>
              {succeeded ? tr('viewConsumptions') : tr('retry')}
            </Button>
          }
        />,
        { toastId, intent: succeeded ? 'success' : 'error', timeout: IMPORT_JOB_TOAST_TIMEOUT_MS }
      );
    }
    showBrowserNotification(title, body, toastId, () => navigate(link));
  };

  const pendingKey = (jobsState?.pendingNotices ?? []).join('|');
  useEffect(() => {
    (jobsState?.pendingNotices ?? []).forEach((id) => {
      if (handled.current.has(id)) return;
      handled.current.add(id);
      const job = jobsState?.jobs[id];
      if (job) notice(job);
      dispatch(actionCreators.AcknowledgeImportJobNotice(id) as any);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey]);

  return null;
};

export default ImportJobsWatcher;
